// ─────────────────────────────────────────────────────────────────────────────
// scriptDirections.ts — WHERE EACH OF THE OWNER'S DIRECTIONS BELONGS.
//
// PURE. A v2 script's `listen_for` carries two kinds of entry
// (APP_CALLS_CONTRACT §1d):
//
//   "Line 5 — STOP. Let him answer."        → beside line L5
//   "If they say “We tried one of those” — Listen. Don't criticise them."
//                                           → inside the branch with that trigger
//
// The owner reads them MID-CALL, so they sit where his eye already is. The
// aside keeps its full list — this places a copy, it removes nothing — and an
// entry this cannot place (no such line, no such branch, any other shape) is
// simply left in the aside where it always was.
//
// ── THE SHAPES ARE THE PRODUCER'S, READ EXACTLY ────────────────────────────
// An em dash, the word "Line", a number. A branch direction matches a trigger
// word for word, or one of its " / " alternatives. No fuzzy matching: a
// direction attached to the wrong branch is worse than one left in the aside.
//
// Maps, not object literals: a line id or a trigger is board data, and a plain
// object lookup walks the prototype chain.
// ─────────────────────────────────────────────────────────────────────────────

const LINE = /^Line\s+(\d+)\s+—\s+([\s\S]+)$/;
const IF_THEY_SAY = /^If they say\s+[“"]([\s\S]+?)[”"]\s+—\s+([\s\S]+)$/;

export interface PlacedDirections {
  /** heading (`L5`) → the directions for that line, in the producer's order. */
  byLine: Map<string, string[]>;
  /** the quoted words → the directions for the branch they trigger. */
  bySaying: Map<string, string[]>;
}

const push = (m: Map<string, string[]>, k: string, v: string) => {
  const list = m.get(k);
  if (list) list.push(v); else m.set(k, [v]);
};

export function placeDirections(listen: unknown): PlacedDirections {
  const byLine = new Map<string, string[]>();
  const bySaying = new Map<string, string[]>();
  for (const raw of Array.isArray(listen) ? listen : []) {
    if (typeof raw !== 'string') continue;
    const s = raw.trim();
    const line = LINE.exec(s);
    if (line) { push(byLine, 'L' + Number(line[1]), line[2].trim()); continue; }
    const say = IF_THEY_SAY.exec(s);
    if (say) push(bySaying, say[1].trim(), say[2].trim());
  }
  return { byLine, bySaying };
}

/** The directions beside one line. */
export function directionsForLine(placed: PlacedDirections, heading: unknown): string[] {
  return typeof heading === 'string' ? placed.byLine.get(heading) || [] : [];
}

/** The directions inside one branch — its whole trigger, or any " / " part of it. */
export function directionsForBranch(placed: PlacedDirections, trigger: unknown): string[] {
  if (typeof trigger !== 'string' || !trigger.trim()) return [];
  const keys = [trigger.trim(), ...trigger.split(' / ').map((p) => p.trim())];
  const out: string[] = [];
  for (const k of Array.from(new Set(keys))) {
    for (const d of placed.bySaying.get(k) || []) out.push(d);
  }
  return out;
}
